/**
 * Cart Share
 * Lets staff copy a link that refills the same cart (variants, quantities and
 * line item properties) for a customer. The link carries the cart itself, so no
 * storage outside Shopify is needed.
 *
 * Also loaded by Node tests (tests/cart-share.test.mjs): the pure helpers are
 * exported via module.exports, the custom elements only register in a browser.
 */
(function () {
  const FORMAT_VERSION = 1;
  const QUERY_PARAM = 'gedeeld';
  // Marks visits from a shared cart in analytics.
  const UTM_PARAMS = { utm_source: 'webshop', utm_medium: 'share-a-cart' };
  const RESULT_KEY = 'cart-share-result';
  const STATUS_VISIBLE_MS = 4000;

  function toBase64Url(text) {
    const bytes = new TextEncoder().encode(text);
    let binary = '';
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function fromBase64Url(code) {
    const base64 = code.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
    return new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)));
  }

  function lineTitle(item) {
    return item.variant_title ? `${item.product_title} · ${item.variant_title}` : item.product_title;
  }

  // Empty properties are dropped: Shopify hides them in the cart anyway.
  function filledProperties(properties) {
    const filled = {};
    Object.entries(properties || {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') filled[key] = String(value);
    });
    return filled;
  }

  /** Turns /cart.js items into a URL-safe code. */
  function encode(items) {
    const lines = items.map((item) => {
      const properties = filledProperties(item.properties);
      const line = [item.variant_id, item.quantity, lineTitle(item)];
      if (Object.keys(properties).length > 0) line.push(properties);
      return line;
    });
    return toBase64Url(JSON.stringify({ v: FORMAT_VERSION, l: lines }));
  }

  function isValidLine(line) {
    if (!Array.isArray(line) || line.length < 3 || line.length > 4) return false;
    const [id, quantity, title, properties = {}] = line;
    return Number.isSafeInteger(id) && id > 0
      && Number.isSafeInteger(quantity) && quantity > 0
      && typeof title === 'string'
      && properties !== null && typeof properties === 'object' && !Array.isArray(properties)
      && Object.values(properties).every((value) => typeof value === 'string');
  }

  /** Returns [{ id, title, quantity, properties }] or null when the code is not a valid shared cart. */
  function decode(code) {
    if (!code) return null;
    try {
      const data = JSON.parse(fromBase64Url(code));
      if (!data || data.v !== FORMAT_VERSION || !Array.isArray(data.l) || data.l.length === 0) return null;
      if (!data.l.every(isValidLine)) return null;
      return data.l.map(([id, quantity, title, properties = {}]) => ({ id, title, quantity, properties }));
    } catch (error) {
      return null;
    }
  }

  /** baseUrl is the shop root including a language folder, e.g. https://www.artiteq.com/de/ */
  function shareUrl(baseUrl, items) {
    const root = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const url = new URL('cart', root);
    url.searchParams.set(QUERY_PARAM, encode(items));
    Object.entries(UTM_PARAMS).forEach(([key, value]) => url.searchParams.set(key, value));
    return url.toString();
  }

  /** The cart page to land on after filling: drops the shared code, keeps utm_* tags. */
  function cartUrlAfterFill(baseUrl, search) {
    const url = new URL('cart', baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`);
    new URLSearchParams(search).forEach((value, key) => {
      if (key.startsWith('utm_')) url.searchParams.set(key, value);
    });
    return url.toString();
  }

  // Same rule as cart-300cm-validator.js, which removes these lines outside artiteq.com NL/BE.
  function is300cm(item) {
    if (!Array.isArray(item.options_with_values)) return false;
    return item.options_with_values.some((option) => {
      const value = (option.value || '').toLowerCase();
      return value.includes('300 cm') || value.includes('300cm') || value.includes('300 cent') || value.includes('300 zent');
    });
  }

  /** texts: { one, other, warning } with __COUNT__ in `other`. The warning only matters when there is a cart to lose. */
  function introText(texts, lineCount, cartIsEmpty) {
    const intro = lineCount === 1 ? texts.one : texts.other.replace('__COUNT__', lineCount);
    return cartIsEmpty ? intro : `${intro} ${texts.warning}`;
  }

  const CartShare = { encode, decode, shareUrl, cartUrlAfterFill, is300cm, introText };

  if (typeof module !== 'undefined' && module.exports) module.exports = CartShare;
  if (typeof window === 'undefined' || typeof customElements === 'undefined') return;
  window.CartShare = CartShare;

  const shopRoot = () => window.location.origin + ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/');

  /**
   * <cart-share-button>: copies the share link (desktop) or opens the phone's
   * share menu (touch devices).
   */
  if (!customElements.get('cart-share-button')) {
    class CartShareButton extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.status = this.querySelector('[role="status"]');
        this.onClick = this.share.bind(this);
        this.button.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.button.removeEventListener('click', this.onClick);
      }

      async share() {
        this.button.disabled = true;
        let url;
        try {
          const cart = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
          if (cart.items.length === 0) return;
          url = shareUrl(shopRoot(), cart.items);

          const isTouch = window.matchMedia('(pointer: coarse)').matches;
          if (isTouch && navigator.share) {
            await navigator.share({ title: this.dataset.shareTitle, url });
            return;
          }
          await navigator.clipboard.writeText(url);
          this.showStatus(this.dataset.copiedText);
        } catch (error) {
          if (error && error.name === 'AbortError') return;
          this.showStatus(this.dataset.copyFailedText, url);
        } finally {
          this.button.disabled = false;
        }
      }

      showStatus(text, url) {
        this.status.textContent = text;
        if (url) {
          const input = document.createElement('input');
          input.type = 'text';
          input.readOnly = true;
          input.value = url;
          input.className = 'cart-share__url';
          input.setAttribute('aria-label', this.dataset.copyFailedText);
          input.addEventListener('focus', () => input.select());
          this.status.appendChild(input);
        }
        this.status.hidden = false;
        this.button.hidden = true;
        // After a copy the button comes back, so a changed cart can be shared again. A failed copy keeps the link visible.
        if (!url) setTimeout(() => this.resetStatus(), STATUS_VISIBLE_MS);
      }

      resetStatus() {
        this.status.hidden = true;
        this.status.textContent = '';
        this.button.hidden = false;
      }
    }

    customElements.define('cart-share-button', CartShareButton);
  }

  /**
   * <cart-share-receiver>: on /cart?gedeeld=... asks the customer to replace
   * their cart, fills it line by line and reports lines that could not be added.
   */
  if (!customElements.get('cart-share-receiver')) {
    class CartShareReceiver extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.notice = this.querySelector('.cart-share__notice');

        const params = new URLSearchParams(window.location.search);
        if (!params.has(QUERY_PARAM)) {
          this.showStoredResult();
          return;
        }

        this.lines = decode(params.get(QUERY_PARAM));
        if (!this.lines) {
          this.removeQueryParam();
          this.showNotice(this.dataset.invalidText, false);
          return;
        }
        this.openDialog();
      }

      openDialog() {
        const list = this.dialog.querySelector('.cart-share__lines');
        this.lines.forEach((line) => {
          const item = document.createElement('li');
          const name = document.createElement('span');
          const quantity = document.createElement('strong');
          name.textContent = line.title;
          quantity.textContent = `${line.quantity}×`;
          item.append(name, quantity);
          list.appendChild(item);
        });
        const cartIsEmpty = this.dataset.cartEmpty === 'true';
        this.dialog.querySelector('.cart-share__intro').textContent = introText(
          { one: this.dataset.introOneText, other: this.dataset.introOtherText, warning: this.dataset.replaceWarningText },
          this.lines.length,
          cartIsEmpty,
        );
        const confirm = this.dialog.querySelector('[data-cart-share-confirm]');
        if (cartIsEmpty) confirm.textContent = this.dataset.confirmAddText;
        this.dialog.querySelector('[data-cart-share-cancel]').addEventListener('click', () => {
          this.removeQueryParam();
          this.dialog.close();
        });
        confirm.addEventListener('click', (event) => this.replaceCart(event.currentTarget));
        this.dialog.showModal();
      }

      async replaceCart(button) {
        button.disabled = true;
        button.textContent = this.dataset.loadingText;
        const validator = document.querySelector('cart-300cm-validator');
        const allow300cm = !validator || validator.getAttribute('data-allow-300cm') === 'true';
        const failed = [];

        await this.post('cart/clear.js', {});
        // One request per line: a combined /cart/add.js call rejects the whole cart when one line fails.
        // Shopify puts each new line on top, so adding in reverse keeps the shared order.
        for (const line of [...this.lines].reverse()) {
          const response = await this.post('cart/add.js', { items: [{ id: line.id, quantity: line.quantity, properties: line.properties }] });
          if (!response.ok) {
            failed.unshift(line.title);
            continue;
          }
          const added = (await response.json()).items[0];
          if (!allow300cm && is300cm(added)) {
            await this.post('cart/change.js', { id: added.key, quantity: 0 });
            failed.unshift(line.title);
          }
        }

        try {
          sessionStorage.setItem(RESULT_KEY, JSON.stringify({ total: this.lines.length, failed }));
        } catch (error) {
          // Private mode without storage: the cart is filled, only the notice is skipped.
        }
        window.location.replace(cartUrlAfterFill(shopRoot(), window.location.search));
      }

      post(path, body) {
        return fetch(`${shopRoot()}${path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
      }

      showStoredResult() {
        let result = null;
        try {
          result = JSON.parse(sessionStorage.getItem(RESULT_KEY));
          sessionStorage.removeItem(RESULT_KEY);
        } catch (error) {
          return;
        }
        if (!result) return;

        if (result.failed.length === 0) {
          this.showNotice(this.dataset.readyText, true);
          return;
        }
        const added = this.dataset.partialText
          .replace('__ADDED__', result.total - result.failed.length)
          .replace('__TOTAL__', result.total);
        const missing = this.dataset.failedText.replace('__ITEMS__', result.failed.join(', '));
        this.showNotice(added, false, missing);
      }

      showNotice(title, success, detail) {
        this.notice.querySelector('.cart-share__notice-title').textContent = title;
        this.notice.querySelector('.cart-share__notice-detail').textContent = detail || '';
        this.notice.classList.toggle('cart-share__notice--success', success);
        this.notice.hidden = false;
      }

      removeQueryParam() {
        const url = new URL(window.location.href);
        url.searchParams.delete(QUERY_PARAM);
        window.history.replaceState({}, '', url);
      }
    }

    customElements.define('cart-share-receiver', CartShareReceiver);
  }
})();
